/**
 * BillingAccountIssue - Support-only page for contract/subscription mismatches.
 *
 * Shown when an account has an active contract but an inactive Stripe subscription.
 * Offers contact support and sign-out — never self-serve reactivation.
 */
import { useEffect } from "react";
import {
  Button,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconHeadset, IconLogout } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "@/constants.ts";
import { initializeIntercom, showIntercom } from "@/utils/intercom";
import { useAuthStore } from "@utils/stores/authStore.ts";
import { loginPathWithRedirect } from "@/pages/LoginManagement/utils/redirect";
import { isContractSubscriptionMismatch } from "@smartwiz/shared-types";

const BillingAccountIssue = (): JSX.Element => {
  const navigate = useNavigate();
  const { user, loading, billingContext, refreshAuth } = useAuthStore();

  useEffect(() => {
    void refreshAuth();
    void initializeIntercom();
  }, [refreshAuth]);

  useEffect(() => {
    if (loading) {
      return;
    }
    if (!user?.email) {
      navigate(loginPathWithRedirect(AppRoutes.billingAccountIssue), {
        replace: true,
      });
      return;
    }
    if (!isContractSubscriptionMismatch(billingContext)) {
      const home = user.useLiteHome ? AppRoutes.homeLite : AppRoutes.home;
      navigate(home, { replace: true });
    }
  }, [billingContext, loading, navigate, user]);

  const handleContactSupport = () => {
    showIntercom();
  };

  const handleLogout = () => {
    navigate(AppRoutes.logout);
  };

  if (loading || !user?.email) {
    return (
      <Center mih="100vh">
        <Loader color="blue" />
      </Center>
    );
  }

  return (
    <Center mih="100vh" px="md" py="xl">
      <Container size="sm">
        <Paper radius="md" p="xl" withBorder shadow="sm">
          <Stack gap="lg" align="stretch">
            <Stack gap="xs">
              <Title order={2}>Your account needs attention</Title>
              <Text c="dimmed">
                Your SmartWiz contract is active, but billing is out of sync
                with your subscription. Our team can restore access — please
                contact support so we can resolve this for you.
              </Text>
            </Stack>

            <Button
              size="md"
              leftSection={<IconHeadset size={18} />}
              onClick={handleContactSupport}
            >
              Contact Support
            </Button>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Do not try to reactivate or purchase a new plan — support will
                align billing with your existing contract.
              </Text>
              <Button
                variant="subtle"
                color="gray"
                size="sm"
                leftSection={<IconLogout size={16} />}
                onClick={handleLogout}
              >
                Sign out
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Container>
    </Center>
  );
};

export default BillingAccountIssue;
